import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  Image,
  Spinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../DoctorForm/DoctorForm.css";
import Avatar from "../../assets/avatar.png";
import CustomModal from "../../components/modal/Modal";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MapPin, Trash2, Info, Plus } from "lucide-react";
import { apiUrl, CONSULTATION_REGISTRATION } from "../../config/appConfig";

// `consultationLink`: rendered by the Tiryaq Professional consultation registration link, which
// submits to its own endpoint (the gateway records the source) and returns to that link.
const DoctorHomeConsultationForm = ({ consultationLink = false }) => {
  const [fullName, setFullName] = useState("");
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicUrl, setProfilePicUrl] = useState("");
  const [hospital, setHospital] = useState("");
  const [specialty, setSpecialty] = useState("");
  // Custom specialty entered by the doctor when "Other" is selected.
  const [customSpecialty, setCustomSpecialty] = useState("");
  // Emergency Doctor Home Consultation: when checked, the specialty is fixed to
  // "Emergency Doctor" and the specialty dropdown is hidden.
  const [isEmergency, setIsEmergency] = useState(false);
  const [onlineConsult, setOnlineConsult] = useState("No");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [yearsOfExperience, setYearOfExperience] = useState("");
  const [validated, setValidated] = useState(false);
  const fileInputRef = useRef(null);
  const [countryCode, setCountryCode] = useState("+964");
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [profilePicError, setProfilePicError] = useState("");
  const [hasCamera, setHasCamera] = useState(true);
  const [hospitals, setHospitals] = useState([
    { name: "", address: "", latitude: null, longitude: null },
  ]);
  const [locationSuggestions, setLocationSuggestions] = useState([[]]);
  const sessionTokenRef = useRef(null);
  const dropdownRefs = useRef([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(true);
  // Ensures the edit pre-fill normalization runs only once after specialties load.
  const specialtyPrefilledRef = useRef(false);
  const [region, setRegion] = useState("Baghdad");

  // Iraqi regions list
  const iraqiRegions = [
    "Baghdad",
    "Basra",
    "Al-Anbar",
    "Babil (Babylon)",
    "Dhi Qar",
    "Al-Qadisiyyah (Diwaniyah)",
    "Diyala",
    "Dohuk",
    "Erbil",
    "Sulaymaniyah",
    "Halabja",
    "Kirkuk",
    "Karbala",
    "Najaf",
    "Nineveh (Mosul)",
    "Salah Al-Din",
    "Wasit",
    "Maysan",
    "Al-Muthanna"
  ];

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      dropdownRefs.current.forEach((ref, index) => {
        if (ref && !ref.contains(event.target)) {
          setLocationSuggestions((prev) => {
            const next = [...prev];
            next[index] = [];
            return next;
          });
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch coordinates from place ID
  const fetchCoordinatesFromPlaceId = async (placeId) => {
    try {
      const { Place } = await window.google.maps.importLibrary("places");
      const place = new Place({ id: placeId });
      await place.fetchFields({ fields: ["location"] });
      
      if (place.location) {
        return {
          latitude: place.location.lat(),
          longitude: place.location.lng(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      return null;
    }
  };

  // Fetch location suggestions
  const fetchLocationSuggestions = async (input, index) => {
    if (!input || input.trim().length < 3) {
      setLocationSuggestions((prev) => {
        const next = [...prev];
        next[index] = [];
        return next;
      });
      return;
    }

    try {
      if (!window.google?.maps) {
        console.error("Google Maps not loaded");
        return;
      }

      const { AutocompleteSuggestion } = await window.google.maps.importLibrary("places");

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }

      const request = {
        input: input,
        sessionToken: sessionTokenRef.current,
      };

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      const formattedSuggestions = suggestions.map((suggestion) => ({
        description: suggestion.placePrediction.text.toString(),
        placeId: suggestion.placePrediction.placeId,
      }));

      setLocationSuggestions((prev) => {
        const next = [...prev];
        next[index] = formattedSuggestions;
        return next;
      });
    } catch (error) {
      console.error("Autocomplete suggestions error", error);
      setLocationSuggestions((prev) => {
        const next = [...prev];
        next[index] = [];
        return next;
      });
    }
  };

  const handleHospitalChange = (index, field, value, skipFetch = false) => {
    const updated = [...hospitals];
    updated[index][field] = value;
    setHospitals(updated);

    // Fetch location suggestions when address field changes
    if (field === "address" && !skipFetch) {
      fetchLocationSuggestions(value, index);
    }
  };

  const handleAddHospital = () => {
    setHospitals([...hospitals, { name: "", address: "", latitude: null, longitude: null }]);
    setLocationSuggestions((prev) => [...prev, []]);
  };

  const handleRemoveHospital = (index) => {
    setHospitals(hospitals.filter((_, i) => i !== index));
    setLocationSuggestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOpenCamera = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoInput = devices.some((device) => device.kind === "videoinput");
      if (!hasVideoInput) {
        setHasCamera(false);
        toast.error("No camera detected on this device.");
        return;
      }
      setHasCamera(true);
      setShowCamera(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setHasCamera(false);
      toast.error("Unable to access camera. Please check permissions or device settings.");
    }
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      const file = new File([blob], "camera-photo.png", { type: "image/png" });
      if (file.size > 10 * 1024 * 1024) {
        setProfilePicError("Captured image is too large. Max size is 10MB.");
        setProfilePic(null);
        setProfilePicUrl("");
        return;
      }
      setProfilePic(file);
      setProfilePicUrl(URL.createObjectURL(file));
      setProfilePicError("");
    });
    const stream = video.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setShowCamera(false);
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      setProfilePicError("Invalid file type. Only JPG, JPEG, PNG allowed.");
      setProfilePic(null);
      setProfilePicUrl("");
      return;
    }
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setProfilePicError("File is too large. Max size is 10MB.");
      setProfilePic(null);
      setProfilePicUrl("");
      return;
    }
    setProfilePic(file);
    setProfilePicUrl(URL.createObjectURL(file));
    setProfilePicError("");
  };

  const countryCodes = [
    { code: "+964", name: "Iraq" }
  ];

  // Fetch specialties from API
  useEffect(() => {
    const fetchSpecialties = async () => {
      setLoadingSpecialties(true);
      try {
        const response = await axios.get(
          apiUrl('/specialties'),
          {
            headers: { accept: "application/json" },
          }
        );

        if (response.data.success && response.data.data?.specialties) {
          setSpecialties(response.data.data.specialties);
        } else {
          toast.error("Failed to load specialties");
          // Fallback to empty array
          setSpecialties([]);
        }
      } catch (error) {
        console.error("Error fetching specialties:", error);
        toast.error("Failed to load specialties");
        // Fallback to empty array
        setSpecialties([]);
      } finally {
        setLoadingSpecialties(false);
      }
    };

    fetchSpecialties();
  }, []);

  // Normalize an incoming specialty value once specialties are loaded.
  // - If it matches a predefined specialty (case-insensitive), align the
  //   dropdown to the canonical option.
  // - If it's a non-empty custom value not in the list, select "Other" and
  //   move the value into the custom-specialty text field (edit pre-fill).
  // Skipped for emergency consultations and the create flow (empty specialty).
  useEffect(() => {
    if (loadingSpecialties || specialtyPrefilledRef.current) return;
    if (isEmergency || !specialty || specialty === "Other") return;

    const match = specialties.find(
      (s) => s.toLowerCase() === specialty.toLowerCase()
    );
    if (match) {
      if (match !== specialty) setSpecialty(match);
    } else {
      setCustomSpecialty(specialty);
      setSpecialty("Other");
    }
    specialtyPrefilledRef.current = true;
  }, [loadingSpecialties, specialties, specialty, isEmergency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false || !profilePic) {
      e.stopPropagation();
      setValidated(true);
      if (!profilePic) setProfilePicError("Profile picture is required.");
      return;
    }
    if (profilePicError) {
      toast.error(profilePicError);
      return;
    }
    // When "Other" is selected the custom specialty text is mandatory.
    // Emergency consultations keep their fixed "Emergency Doctor" specialty.
    const isOtherSpecialty = !isEmergency && specialty === "Other";
    const finalSpecialty = isOtherSpecialty ? customSpecialty.trim() : specialty;
    if (isOtherSpecialty && !finalSpecialty) {
      e.stopPropagation();
      setValidated(true);
      toast.error("Please enter your specialty");
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", profilePic);
      const uploadResponse = await axios.post(apiUrl('/professionals/photo'), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const profilePictureUrl = uploadResponse.data.data.key;
      const doctorData = {
        role_id: 6,
        full_name: fullName,
        hospital_details: hospitals
          .filter((h) => h.name.trim() !== "" && h.address.trim() !== "")
          .map((h) => ({ 
            name: h.name.trim(), 
            address: h.address.trim(),
            latitude: h.latitude,
            longitude: h.longitude
          })),
        specialty: finalSpecialty.toLowerCase(),
        online_consultation: onlineConsult === "Yes",
        contact_phone: `${countryCode}${phone}`,
        profile_picture_url: profilePictureUrl,
        short_bio: bio,
        years_of_experience: parseInt(yearsOfExperience),
        region: region,
      };
      const createPath = consultationLink ? CONSULTATION_REGISTRATION.CREATE_PATH : '/professionals';
      await axios.post(apiUrl(createPath), doctorData, {
        headers: { "Content-Type": "application/json" },
      });
      console.log('Form submitted successfully!');
      console.log('REACT_APP_FRONTEND_URL:', process.env.REACT_APP_FRONTEND_URL);
      setShowModal(true);
      setFullName("");
      setProfilePic(null);
      setProfilePicUrl("");
      setHospital("");
      setHospitals([{ name: "", address: "", latitude: null, longitude: null }]);
      setLocationSuggestions([[]]);
      setSpecialty("");
      setCustomSpecialty("");
      setIsEmergency(false);
      setOnlineConsult("No");
      setPhone("");
      setBio("");
      setYearOfExperience("");
      setRegion("Baghdad");
      setValidated(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast.success("Your details have been saved successfully");
    } catch (error) {
      console.error("Error uploading data:", error);
      toast.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="main-container">
        <main className="main-content">
          <div className="content-wrapper doctor-form-wide">
            <div className="login-form-container">
              <h2 className="text-center">Doctor Home Consultation Details</h2>
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                {/* Profile Picture - top header */}
                <div className="doctor-profile-header">
                  <Image
                    src={profilePicUrl || Avatar}
                    roundedCircle
                    width={120}
                    height={120}
                    alt="Profile Preview"
                    className="doctor-profile-avatar"
                  />
                  <span className="doctor-profile-title">
                    Profile Picture<span className="required-asterisk">*</span>
                  </span>
                  <div className="doctor-profile-actions">
                    <Button variant="outline-primary" onClick={() => fileInputRef.current?.click()}>
                      Upload from Device
                    </Button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleProfilePicChange}
                    />
                    <Button variant="outline-primary" onClick={handleOpenCamera}>
                      Open Camera
                    </Button>
                  </div>
                  {profilePicError && <div className="invalid-feedback d-block">{profilePicError}</div>}
                  {showCamera && (
                    <div className="doctor-profile-camera">
                      {hasCamera ? (
                        <>
                          <video ref={videoRef} style={{ width: "100%", maxHeight: 300 }} />
                          <div className="d-flex justify-content-between mt-2">
                            <Button variant="success" onClick={handleCapturePhoto}>
                              Capture
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() => {
                                const stream = videoRef.current?.srcObject;
                                if (stream) stream.getTracks().forEach((track) => track.stop());
                                setShowCamera(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                          <canvas ref={canvasRef} style={{ display: "none" }} />
                        </>
                      ) : (
                        <div className="text-danger mt-2">Camera not available on this device.</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Row 1: Full Name | Specialty | Years of Experience */}
                <Row>
                  <Col md={4}>
                    <Form.Group className="form-group" controlId="doctorFullName">
                      <Form.Label>
                        Doctor Full Name
                        <span className="required-asterisk">*</span>
                      </Form.Label>
                      <Form.Control
                        className="form-input"
                        type="text"
                        placeholder="Enter your full name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Full name is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    {!isEmergency && (
                      <Form.Group className="form-group" controlId="specialty">
                        <Form.Label>
                          Specialty<span className="required-asterisk">*</span>
                        </Form.Label>
                        <div className="select-wrapper">
                          <Form.Select
                            className="form-select"
                            value={specialty}
                            onChange={(e) => setSpecialty(e.target.value)}
                            required
                            disabled={loadingSpecialties}
                          >
                            <option value="">
                              {loadingSpecialties ? "Loading specialties..." : "Select specialty"}
                            </option>
                            {specialties.map((spec) => (
                              <option key={spec} value={spec}>
                                {spec}
                              </option>
                            ))}
                            <option value="Other">Other</option>
                          </Form.Select>
                          <span className="select-icon">&#9662;</span>
                        </div>
                        {specialty === "Other" && (
                          <Form.Control
                            className="form-input"
                            type="text"
                            placeholder="Enter your specialty"
                            value={customSpecialty}
                            onChange={(e) => setCustomSpecialty(e.target.value)}
                            required
                            style={{ marginTop: "0.5rem" }}
                          />
                        )}
                        <Form.Control.Feedback type="invalid">
                          Specialty is required
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                  </Col>

                  <Col md={4}>
                    <Form.Group className="form-group" controlId="experience">
                      <Form.Label>
                        Years of Experience
                        <span className="required-asterisk">*</span>
                      </Form.Label>
                      <Form.Control
                        className="form-input"
                        type="number"
                        placeholder="Enter Years of Experience"
                        value={yearsOfExperience}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || parseInt(val) >= 0) setYearOfExperience(val);
                        }}
                        required
                        min={0}
                      />
                      <Form.Control.Feedback type="invalid">
                        Years of experience must be 0 or greater
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Row 2: Contact Phone | Region */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="form-group form-group-check" controlId="contactPhone">
                      <Form.Label>
                        Contact Phone
                        <span className="required-asterisk">*</span>
                      </Form.Label>
                      <Row className="g-2">
                        <Col xs={5}>
                          <Form.Select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} required style={{ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {countryCodes.map((c, index) => (
                              <option key={`${c.code}-${c.name}-${index}`} value={c.code}>
                                {c.code} - {c.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col xs={7}>
                          <Form.Control
                            className="form-input"
                            type="tel"
                            placeholder="Enter phone"
                            value={phone}
                            onChange={(e) => {
                              let val = e.target.value;
                              if (!/^[0-9]*$/.test(val)) return;
                              setPhone(val);
                            }}
                            required
                            pattern="^[0-9]{8,15}$"
                          />
                        </Col>
                      </Row>
                      <Form.Control.Feedback type="invalid">
                        Please enter a valid phone number (8–15 digits)
                      </Form.Control.Feedback>
                      <div className="phone-note-banner">
                        <Info size={18} />
                        <span>
                          Please enter your personal mobile number. This number will
                          be used for management purposes only.
                        </span>
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="form-group" controlId="region">
                      <Form.Label>
                        Region<span className="required-asterisk">*</span>
                      </Form.Label>
                      <div className="select-wrapper">
                        <Form.Select
                          className="form-select"
                          value={region}
                          onChange={(e) => setRegion(e.target.value)}
                          required
                        >
                          {iraqiRegions.map((regionName) => (
                            <option key={regionName} value={regionName}>
                              {regionName}
                            </option>
                          ))}
                        </Form.Select>
                        <span className="select-icon">&#9662;</span>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Short Bio - full width */}
                <Form.Group className="form-group" controlId="bio">
                  <Form.Label>
                    Short Bio<span className="required-asterisk">*</span>
                  </Form.Label>
                  <Form.Control
                    className="form-input"
                    as="textarea"
                    rows={3}
                    placeholder="Tell us about your experience"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Short bio is required
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="form-group" controlId="hospitalName">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Form.Label className="mb-0">
                      Home Location<span className="required-asterisk">*</span>
                    </Form.Label>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="add-hospital-btn"
                      onClick={handleAddHospital}
                      title="Add Another Hospital"
                    >
                      <Plus size={16} />
                    </Button>
                  </div>
                  {hospitals.map((item, index) => (
                    <div key={index} className="mb-3 border p-3 rounded">
                      <Row className="g-2 align-items-start">
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Name"
                            value={item.name}
                            onChange={(e) => handleHospitalChange(index, "name", e.target.value)}
                            required
                          />
                        </Col>
                        <Col md>
                      {/* Location with autocomplete */}
                      <div
                        style={{ position: "relative" }}
                        ref={(el) => (dropdownRefs.current[index] = el)}
                      >
                        <div style={{ position: "relative" }}>
                          <MapPin
                            size={18}
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#6c757d",
                              zIndex: 1,
                              pointerEvents: "none"
                            }}
                          />
                          <Form.Control
                            type="text"
                            placeholder="Search location..."
                            value={item.address}
                            onChange={(e) => handleHospitalChange(index, "address", e.target.value)}
                            required
                            autoComplete="off"
                            style={{
                              paddingLeft: "40px",
                              borderRadius: locationSuggestions[index]?.length > 0 ? "8px 8px 0 0" : "8px",
                              border: locationSuggestions[index]?.length > 0 ? "1px solid #0d6efd" : "1px solid #ced4da",
                              transition: "all 0.2s ease"
                            }}
                          />
                        </div>
                        {locationSuggestions[index]?.length > 0 && (
                          <div
                            style={{
                              position: "absolute",
                              zIndex: 1000,
                              width: "100%",
                              backgroundColor: "#fff",
                              border: "1px solid #dee2e6",
                              borderTop: "none",
                              borderRadius: "0 0 8px 8px",
                              maxHeight: 280,
                              overflowY: "auto",
                              boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
                            }}
                          >
                            {locationSuggestions[index].map((suggestion, idx) => (
                              <div
                                key={suggestion.placeId}
                                style={{
                                  padding: "12px 16px",
                                  cursor: "pointer",
                                  borderBottom: idx === locationSuggestions[index].length - 1 ? "none" : "1px solid #f0f0f0",
                                  background: "#fff",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "12px",
                                  transition: "all 0.2s ease",
                                }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  handleHospitalChange(index, "address", suggestion.description, true);
                                  const coords = await fetchCoordinatesFromPlaceId(suggestion.placeId);
                                  if (coords) {
                                    setHospitals((prev) => {
                                      const updated = [...prev];
                                      updated[index] = {
                                        ...updated[index],
                                        latitude: coords.latitude,
                                        longitude: coords.longitude,
                                      };
                                      return updated;
                                    });
                                  }
                                  setLocationSuggestions((prev) => {
                                    const next = [...prev];
                                    next[index] = [];
                                    return next;
                                  });
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#f8f9fa";
                                  e.currentTarget.style.paddingLeft = "20px";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "#fff";
                                  e.currentTarget.style.paddingLeft = "16px";
                                }}
                              >
                                <MapPin
                                  size={18}
                                  style={{
                                    color: "#0d6efd",
                                    marginTop: "2px",
                                    flexShrink: 0
                                  }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    fontSize: "14px",
                                    color: "#212529",
                                    lineHeight: "1.5",
                                    fontWeight: "400"
                                  }}>
                                    {suggestion.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                        </Col>
                        {hospitals.length > 1 && (
                          <Col xs="auto" className="d-flex align-items-start">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveHospital(index)}
                              title="Remove Hospital"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </Col>
                        )}
                      </Row>
                    </div>
                  ))}
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="login-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Form>
            </div>
          </div>
        </main>
      </div>
      <CustomModal 
        showModal={showModal} 
        onClose={() => setShowModal(false)} 
        autoRedirect={true}
        redirectUrl={consultationLink ? CONSULTATION_REGISTRATION.RETURN_URL : process.env.REACT_APP_FRONTEND_URL}
      />
    </>
  );
};

export default DoctorHomeConsultationForm;


